import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import ForceChangePassword from "./components/ForceChangePassword";
import AuthCallback from "./pages/AuthCallback";
import Board from "./pages/Board";
import Analytics from "./pages/Analytics";
import BulkAssign from "./pages/BulkAssign";
import Users from "./pages/Users";
import Research from "./pages/Research";
import Settings from "./pages/Settings";
import HotLeads from "./pages/HotLeads";
import AiStars from "./pages/AiStars";
import AIInsights from "./pages/AIInsights";
import ActivityMonitor from "./pages/ActivityMonitor";
import Performance from "./pages/Performance";
import TodaysActions from "./pages/TodaysActions";
import OrderPipeline from "./pages/OrderPipeline";
import { VIEW_ALL_ROLES, CAN_ASSIGN_ROLES, canViewHotLeads, canViewAI, isSuperAdmin } from "./lib/states";

function Protected({ children, allowedRoles }) {
  const { user, loading, mustChangePassword } = useAuth();
  if (loading)
    return <div className="h-screen flex items-center justify-center text-stone-400" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <ForceChangePassword />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function HotLeadsGuard({ children }) {
  const { user } = useAuth();
  if (!canViewHotLeads(user)) return <Navigate to="/" replace />;
  return children;
}

function AIInsightsGuard({ children }) {
  const { user } = useAuth();
  if (!canViewAI(user)) return <Navigate to="/" replace />;
  return children;
}

function SuperAdminGuard({ children }) {
  const { user } = useAuth();
  if (!isSuperAdmin(user)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Board />} />
              <Route path="/today" element={<TodaysActions />} />
              <Route path="/order-pipeline" element={<OrderPipeline />} />
              <Route path="/hot" element={<HotLeadsGuard><HotLeads /></HotLeadsGuard>} />
              <Route path="/ai-stars" element={<AIInsightsGuard><AiStars /></AIInsightsGuard>} />
              <Route path="/ai-insights" element={<AIInsightsGuard><AIInsights /></AIInsightsGuard>} />
              <Route path="/activity-monitor" element={<SuperAdminGuard><ActivityMonitor /></SuperAdminGuard>} />
              <Route path="/research" element={<Protected allowedRoles={["admin", "super_admin"]}><Research /></Protected>} />
              <Route path="/performance" element={<Protected allowedRoles={["admin", "super_admin"]}><Performance /></Protected>} />
              <Route path="/analytics" element={<Protected allowedRoles={["admin", "super_admin"]}><Analytics /></Protected>} />
              <Route path="/assign" element={<Protected allowedRoles={CAN_ASSIGN_ROLES}><BulkAssign /></Protected>} />
              <Route path="/users" element={<Protected allowedRoles={["admin", "super_admin"]}><Users /></Protected>} />
              <Route path="/settings" element={<Protected allowedRoles={["admin", "super_admin"]}><Settings /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
